/**
 * The pure half of Studio's analytics: sessions and batching, with the clock,
 * ids, storage and network injected so it can be tested without a browser.
 * `analytics.ts` wires it to the page.
 */

/** A session ends after this long without a tracked event. */
export const SESSION_IDLE_MS = 30 * 60_000;
/** Queued events are sent at least this often. */
export const FLUSH_INTERVAL_MS = 5000;
/** A queue this long is sent right away. */
export const FLUSH_AT = 20;
/** The API accepts at most this many events per request. */
export const MAX_BATCH = 50;
/** Oldest events are dropped beyond this (e.g. while offline). */
export const MAX_QUEUE = 500;

export type PropertyValue = string | number | boolean | null | string[];
export type EventProperties = Record<string, PropertyValue | undefined>;

export interface QueuedEvent {
	at: number;
	name: string;
	path?: string;
	properties?: Record<string, PropertyValue>;
	sessionId: string;
}

export interface Session {
	id: string;
	lastActivity: number;
}

/** The current session, or a new one if there is none or it went idle. */
export function resolveSession(
	current: Session | null,
	now: number,
	newId: () => string
): { session: Session; started: boolean } {
	if (current && now - current.lastActivity <= SESSION_IDLE_MS) {
		return { session: { id: current.id, lastActivity: now }, started: false };
	}
	return { session: { id: newId(), lastActivity: now }, started: true };
}

/**
 * Split the next request off the front of the queue: up to `max` events, all
 * from the same session (each request carries one session id).
 */
export function takeBatch(
	queue: QueuedEvent[],
	max = MAX_BATCH
): { batch: QueuedEvent[]; rest: QueuedEvent[] } {
	const [first] = queue;
	if (!first) {
		return { batch: [], rest: [] };
	}
	let end = 0;
	while (
		end < queue.length &&
		end < max &&
		queue[end]?.sessionId === first.sessionId
	) {
		end += 1;
	}
	return { batch: queue.slice(0, end), rest: queue.slice(end) };
}

/** Drop undefined values; keep flat primitives and string arrays. */
export function cleanProperties(
	properties: EventProperties | undefined
): Record<string, PropertyValue> | undefined {
	if (!properties) {
		return;
	}
	const out: Record<string, PropertyValue> = {};
	let kept = 0;
	for (const [key, value] of Object.entries(properties)) {
		if (value !== undefined) {
			out[key] = value;
			kept += 1;
		}
	}
	return kept > 0 ? out : undefined;
}

export interface QueueOptions {
	/**
	 * A session id owned by someone else (Pluck Desktop's main process, one
	 * per launch). When set it replaces the idle rollover.
	 */
	externalSessionId?: () => string | undefined;
	/** Read the stored session (may return null). */
	loadSession: () => Session | null;
	newId: () => string;
	now: () => number;
	/** Called (before the event) whenever a new session starts. */
	onSessionStart?: (session: Session) => void;
	saveSession: (session: Session) => void;
	send: (sessionId: string, events: QueuedEvent[]) => Promise<void>;
}

export interface AnalyticsQueue {
	/** Take everything queued, as request-sized batches, without sending. */
	drain: () => QueuedEvent[][];
	enqueue: (
		name: string,
		properties?: EventProperties,
		path?: string
	) => QueuedEvent;
	/** Send everything queued, one request per batch. Never rejects. */
	flush: () => Promise<void>;
	size: () => number;
}

export function createAnalyticsQueue(options: QueueOptions): AnalyticsQueue {
	let queue: QueuedEvent[] = [];
	let session: Session | null = null;
	let flushing: Promise<void> | null = null;

	const currentSession = (): Session => {
		const now = options.now();
		const previous = session ?? options.loadSession();
		const external = options.externalSessionId?.();
		const resolved = external
			? {
					session: { id: external, lastActivity: now },
					started: previous?.id !== external,
				}
			: resolveSession(previous, now, options.newId);
		({ session } = resolved);
		options.saveSession(session);
		if (resolved.started) {
			options.onSessionStart?.(session);
		}
		return session;
	};

	const drain = (): QueuedEvent[][] => {
		const batches: QueuedEvent[][] = [];
		while (queue.length > 0) {
			const { batch, rest } = takeBatch(queue);
			batches.push(batch);
			queue = rest;
		}
		return batches;
	};

	const sendAll = async (): Promise<void> => {
		while (queue.length > 0) {
			const { batch, rest } = takeBatch(queue);
			queue = rest;
			const sessionId = batch[0]?.sessionId ?? "";
			try {
				// biome-ignore lint/performance/noAwaitInLoops: batches must reach the server in order, one request at a time
				await options.send(sessionId, batch);
			} catch {
				// Analytics is best-effort: drop the batch rather than retry forever.
			}
		}
	};

	const flush = (): Promise<void> => {
		if (!flushing) {
			flushing = sendAll().finally(() => {
				flushing = null;
			});
		}
		return flushing;
	};

	const enqueue = (
		name: string,
		properties?: EventProperties,
		path?: string
	): QueuedEvent => {
		const { id } = currentSession();
		const event: QueuedEvent = {
			name,
			at: options.now(),
			sessionId: id,
			properties: cleanProperties(properties),
			path,
		};
		queue.push(event);
		if (queue.length > MAX_QUEUE) {
			queue = queue.slice(queue.length - MAX_QUEUE);
		}
		return event;
	};

	return { enqueue, flush, drain, size: () => queue.length };
}
